import {
	Button,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Flex,
	Text,
	Divider,
	Box,
	FormControl,
	FormLabel,
	Input,
	FormErrorMessage,
	useBreakpointValue,
	Grid,
	GridItem,
	Image,
	Link,
} from '@chakra-ui/react'
import type { ChakraProps, ThemingProps } from '@chakra-ui/react'
import { FC, PropsWithChildren, useCallback, useEffect } from 'react'
import { useState, useMemo } from 'react'
import {
	ArrowLeftIcn,
	GoogleSignInButton,
	MicrosoftSignInButton,
	IcnClock,
	SingleNeutralCircleBoldIcn,
	IcnLightStick,
	StarVerifiedIcon,
} from '@alexanderathoodly/ui-library'
import { useSignInUpForm } from '../auth/usesigninupform'
import { useForm, UseFormReturn } from 'react-hook-form'
import { UseMutateAsyncFunction, useMutation, useQuery } from 'react-query'
import {
	getProviderQuery,
	getRoomQueries,
} from '~/routes/coworking_location/queries'
import { CALENDLY_LINK } from '~/settings'
import { useCoworkingSerpParams } from '~/routes/coworking_serp/utils'
import { formatInt } from '@alexanderathoodly/data-models'
import type {
	LocationInterestPayload,
	MoveInDateForInterest,
	LocationResult,
	CoworkingSolution,
	ServiceOfficeMembershipResult,
} from '@alexanderathoodly/data-models'
import { getGA4ClientId } from './utils'
import { sendGA4Event } from '~/analytics/ga'
import { apiClient } from '~/utils/requests'
import { useTranslation, Trans } from 'react-i18next'
import SolutionMap from '~/maps/floorplanmap'
import {
	useSolutionFloorId,
	useFloorsWithBounds,
} from '~/routes/coworking_location/utils'
import { BookShowingModalContent } from '~/forms/showingrequestform'
import type { BookShowingModalContentProps } from '~/forms/showingrequestform'
import { useParallelQueries } from '~/queries/useparallelqueries'
import { servicesQuery } from '~/routes/coworking_location/queries'
import {
	usePersonOfTheDay,
	FlexOfficeResultWithCheapestOffer,
} from '~/forms/utils'
import { useHistory, useLocation } from 'react-router-dom'
import { useUser } from 'reactfire'

interface FormValues {
	moveInDate: MoveInDateForInterest
	minWorkspaces: number
	maxWorkspaces: number
	phoneNumber: string
	email: string
	password: string
}

const useShowPriceForm = (
	location: LocationResult,
	createAccountLocation:
		| 'solution list'
		| 'membership'
		| 'conversion box'
		| 'flex office'
) => {
	const { pathname, search } = useLocation()
	const history = useHistory()
	const currentStep = useMemo(() => {
		const params = new URLSearchParams(search)
		const step = params.get('step')
		if (!step) {
			return 1
		}
		return isNaN(parseInt(step)) ? 1 : parseInt(step)
	}, [search])

	const LAST_STEP = 4
	const [isSignInFormShown, setIsSignInFormShown] = useState(false)
	const showSignUpForm = useCallback(() => {
		setIsSignInFormShown(false)
	}, [])
	const showSignInForm = useCallback(() => {
		setIsSignInFormShown(true)
	}, [])
	const goToStep = useCallback(
		(next: boolean) => {
			const params = new URLSearchParams(search)
			params.delete('step')
			const newStep = next ? currentStep + 1 : currentStep - 1
			params.append('step', newStep.toString())
			history.replace(`${pathname}?${params.toString()}`)
		},
		[currentStep, history, pathname, search]
	)
	const goToNextStep = useCallback(() => goToStep(true), [goToStep])
	const goToPreviousStep = useCallback(() => goToStep(false), [goToStep])

	const params = useCoworkingSerpParams()
	/**
	 * We have decided to reuse Interest and its API because the fields
	 * have been very close to what we want for lead to be createdted
	 * through creatingaccount to see hidden prices. Moreover, this is
	 * experimental feature so we did not want to touch the exisiting
	 * models and APIs.
	 *  */
	const interestMutation = useMutation({
		mutationFn: async (data: FormValues) => {
			const gaClientId = await getGA4ClientId()
			const payload: LocationInterestPayload = {
				location: location.id,
				email: data.email,
				phone_number: data.phoneNumber,
				ga_client_id: gaClientId,
				size_segment: { min: data.minWorkspaces, max: data.maxWorkspaces },
				move_in_date: data.moveInDate,
				monthly_budget: params.budget || 0,
				comment: createAccountLocation,
			}
			return await apiClient.sendLocationInterest(payload)
		},
		onSuccess: (data: LocationInterestPayload) => {
			localStorage.setItem('phoneNumber', data.phone_number)
		},
	})

	return {
		currentStep,
		lastStep: LAST_STEP,
		isSignInFormShown,
		showSignUpForm,
		showSignInForm,
		goToNextStep,
		goToPreviousStep,
		sendInterest: interestMutation.mutateAsync,
	}
}

const ChoiseCard: FC<
	ChakraProps & {
		mainText: string
		subText: string
		onClick: () => void
		isSelected: boolean
		theme?: string
	}
> = ({ mainText, subText, onClick, isSelected, theme = 'normal', ...rest }) => {
	const [isHovered, setIsHovered] = useState(false)
	return (
		<Box
			as="button"
			type="button"
			borderRadius="5px"
			boxShadow="base"
			bgColor={isHovered || isSelected ? 'blue.500' : 'white'}
			w="full"
			onClick={onClick}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			_hover={{ bgColor: 'blue.500' }}
			py={3}
			{...rest}
		>
			<Text
				color={
					isHovered || isSelected
						? 'white'
						: theme == 'gray'
						? 'blackAlpha.600'
						: 'blue.500'
				}
				fontSize="2xl"
				fontWeight="semibold"
			>
				{mainText}
			</Text>
			<Text
				fontSize="xs"
				color={isHovered || isSelected ? 'white' : 'blackAlpha.800'}
			>
				{subText}
			</Text>
		</Box>
	)
}

const IntroductionText: FC<ChakraProps & { city: string }> = ({
	city,
	...rest
}) => {
	const { t } = useTranslation()
	return (
		<Flex alignItems="center" gap={{ base: 2, md: 3 }} {...rest}>
			<IcnClock
				width={6}
				height={6}
				display={{ base: 'none', md: 'inline-block' }}
			/>
			<Text color="blackAlpha.700">
				{t(
					'Få direkt access till dagsaktuella priser för alla coworkinganläggningar i {{city}}, dina uppgifter blir inte synliga för operatören.',
					{ city }
				)}
			</Text>
		</Flex>
	)
}

const ContentFooter: FC<ChakraProps & PropsWithChildren> = ({
	children,
	...rest
}) => {
	return (
		<Flex
			display="flex"
			gap={4}
			alignItems="center"
			fontSize={{ base: 'xs', md: 'sm' }}
			color="blackAlpha.700"
			{...rest}
		>
			<Box backgroundColor="white">
				<SingleNeutralCircleBoldIcn
					w={8}
					h={8}
					backgroundColor="blue.500"
					borderRadius="full"
					sx={{ path: { fill: 'white' } }}
				/>
			</Box>
			<Text>{children}</Text>
		</Flex>
	)
}

interface CommonProps {
	goToNext: () => void
	form: UseFormReturn<FormValues, any>
}

const MOVE_IN_DATE_CHOICES: Array<{
	mainText: string
	subText: string
	value: MoveInDateForInterest
}> = [
	{
		mainText: 'Ja',
		subText: 'omgående',
		value: 'soon',
	},
	{
		mainText: 'Snart',
		subText: 'om 3-12 månader',
		value: 'after_3_to_12_months',
	},
	{
		mainText: 'Nej',
		subText: 'tittar bara',
		value: 'not_decided_yet',
	},
]

const MoveInDateForm: FC<
	CommonProps & {
		city: string
		createAccountLocation:
			| 'solution list'
			| 'membership'
			| 'conversion box'
			| 'flex office'
	}
> = ({ goToNext, form, city, createAccountLocation }) => {
	const { t } = useTranslation()
	const setFormValue = useCallback(
		(value: MoveInDateForInterest) => {
			form.setValue('moveInDate', value)
			if (typeof window !== 'undefined') {
				localStorage.setItem('interest_moveInDate', value)
			}
		},
		[form]
	)
	const [error, setError] = useState<string | undefined>(undefined)
	const formValues = form.watch('moveInDate')
	const onClick = useCallback(() => {
		if (!formValues) {
			setError(t('Du behöver välja ett av alternativen ovan.'))
		} else {
			sendGA4Event('initiate_price_account', { step: 'intent_chosen' })
			goToNext()
		}
	}, [formValues, goToNext, t])
	return (
		<>
			<IntroductionText city={city} mb={{ base: 4, md: 6 }} />
			<Text fontSize="lg" fontWeight="bold" mb={{ base: 3, md: 8 }}>
				{t('Är ni på jakt efter ett nytt kontor?')}
			</Text>
			<Flex
				direction={{ base: 'column', md: 'row' }}
				justifyContent="space-between"
				gap={3}
				mb={8}
			>
				{MOVE_IN_DATE_CHOICES.map((choice) => (
					<ChoiseCard
						key={choice.mainText}
						mainText={t(choice.mainText)}
						subText={t(choice.subText)}
						isSelected={formValues == choice.value}
						onClick={() => setFormValue(choice.value)}
						theme={choice.mainText == 'Nej' ? 'gray' : 'normal'}
					/>
				))}
			</Flex>
			{error && <Text color="red.500">{error}</Text>}
			<Button w="full" onClick={onClick} mb={{ base: 5, md: 10 }}>
				{createAccountLocation === 'conversion box'
					? t('Nästa steg för att få offert')
					: t('Nästa steg för att se pris')}
			</Button>
			<ContentFooter>
				{t(
					'Med ett konto kan du se priser och boka visningar - men även ta kostnadsfri hjälp av våra kontorsexperter för att snabbt få förslag på passande kontor för ditt team.'
				)}
			</ContentFooter>
		</>
	)
}

const COMPANY_SIZE_CHOICES = [
	{ label: '1-2', min: 1, max: 2 },
	{ label: '3-5', min: 3, max: 5 },
	{ label: '6-10', min: 6, max: 10 },
	{ label: '11-20', min: 11, max: 20 },
	{ label: '21-50', min: 21, max: 50 },
	{ label: '50+', min: 51, max: Infinity },
]

const CompanySizeForm: FC<CommonProps & { city: string }> = ({
	goToNext,
	form,
	city,
}) => {
	const { t } = useTranslation()
	const setFormValue = useCallback(
		(min: number, max: number) => {
			form.setValue('minWorkspaces', min)
			form.setValue('maxWorkspaces', max)
			if (typeof window !== 'undefined') {
				localStorage.setItem(
					'interest_workspaces',
					JSON.stringify({ minWorkspaces: min, maxWorkspaces: max })
				)
			}
		},
		[form]
	)
	const [error, setError] = useState<string | undefined>(undefined)
	const formValues = form.watch('maxWorkspaces')
	const onClick = useCallback(() => {
		if (!formValues) {
			setError(t('Du behöver välja ett av alternativen ovan.'))
		} else {
			sendGA4Event('initiate_price_account', { step: 'size_chosen' })
			goToNext()
		}
	}, [formValues, goToNext, t])
	return (
		<>
			<IntroductionText city={city} mb={{ base: 4, md: 6 }} />
			<Text fontSize="lg" fontWeight="bold" mb={{ base: 3, md: 8 }}>
				{t('Hur många anställda är ni på företaget?')}
			</Text>
			<Flex
				direction={{ base: 'row', md: 'row' }}
				flexWrap={{ base: 'wrap', md: 'nowrap' }}
				justifyContent="space-between"
				gap={3}
				mb={8}
			>
				{COMPANY_SIZE_CHOICES.map((choice) => (
					<ChoiseCard
						key={choice.label}
						mainText={choice.label}
						subText={t('person', { count: 2 })}
						w={{ base: '45%', md: '100%' }}
						isSelected={form.getValues('maxWorkspaces') == choice.max}
						onClick={() => setFormValue(choice.min, choice.max)}
					/>
				))}
			</Flex>
			{error && <Text color="red.500">{error}</Text>}
			<Button w="full" onClick={onClick} mb={{ base: 5, md: 10 }}>
				{t('Nästa steg för att se pris')}
			</Button>
			<ContentFooter>
				{t(
					'Ta hjälp av våra kontorsexperter för att se till att ni hittar bästa möjliga kontor för ert team, helt kostnadsfritt.'
				)}
			</ContentFooter>
		</>
	)
}

const SignUpForm: FC<
	CommonProps & {
		sendInterest: UseMutateAsyncFunction<
			LocationInterestPayload,
			unknown,
			FormValues,
			unknown
		>
	}
> = ({ goToNext, form, sendInterest }) => {
	const { t } = useTranslation()
	const { passwordSignUp, googleSignIn, microsoftSignIn } = useSignInUpForm({
		defaultType: 'signUp',
	})
	const [error, setError] = useState<Error>()
	const onSubmit = useCallback(
		async (data: FormValues) => {
			try {
				const result = await passwordSignUp(data.email, data.password)
				if ('message' in result) {
					// This means that result is Error
					throw result
				}
				await sendInterest(data)
				goToNext()
			} catch (e) {
				setError(e as Error)
			}
		},
		[passwordSignUp, goToNext, sendInterest]
	)
	return (
		<>
			<Text fontSize="lg" fontWeight="bold" mb={2}>
				{t('Ett steg kvar för att se pris - skapa kontouppgifter')}
			</Text>
			<FormControl
				isInvalid={!!form.formState.errors.phoneNumber}
				isRequired
				mb={{ base: 3, md: 4 }}
			>
				<FormLabel
					mb={1}
					fontWeight="semibold"
					color="blackAlpha.700"
					fontSize="sm"
				>
					{t('Telefonnummer')}
				</FormLabel>
				<Input
					type="tel"
					placeholder="070 456 789"
					{...form.register('phoneNumber', {
						required:
							t('Det krävs telefonnummer för att skapa ett konto.') ||
							'Det krävs telefonnummer för att skapa ett konto.',
					})}
					borderColor="blackAlpha.600"
				/>
				{form.formState.errors.phoneNumber && (
					<FormErrorMessage my={3}>
						{form.formState.errors.phoneNumber.message}
					</FormErrorMessage>
				)}
			</FormControl>
			<Divider bgColor="blackAlpha.300" mb={3} />
			<Text fontWeight="semibold" color="blackAlpha.700" mb={2}>
				{t('Inloggningsuppgifter')}
			</Text>
			<Flex
				direction={{ base: 'column', md: 'row' }}
				gap={{ base: 1, md: 3 }}
				mb={{ base: 6, md: 3 }}
			>
				<GoogleSignInButton
					type="submit"
					h="2.625rem"
					mb={1}
					onClick={async () => {
						try {
							if (!form.getValues('phoneNumber')) {
								throw new Error(
									t('Det krävs telefonnummer för att skapa ett konto.')
								)
							}
							const result = await googleSignIn()
							if ('user' in result) {
								await sendInterest({
									...form.getValues(),
									email: result.user.email || '',
									phoneNumber: form.getValues('phoneNumber'),
								})
								goToNext()
							} else {
								throw result
							}
						} catch (e) {
							setError(e as Error)
						}
					}}
				/>
				<MicrosoftSignInButton
					type="submit"
					h="2.625rem"
					onClick={async () => {
						try {
							if (!form.getValues('phoneNumber')) {
								throw new Error(
									t('Det krävs telefonnummer för att skapa ett konto.')
								)
							}
							const result = await microsoftSignIn()
							if ('user' in result) {
								await sendInterest({
									...form.getValues(),
									email: result.user.email || '',
									phoneNumber: form.getValues('phoneNumber'),
								})
								goToNext()
							} else {
								throw result
							}
						} catch (e) {
							setError(e as Error)
						}
					}}
				/>
			</Flex>
			<Text color="blackAlpha.700" mb={2}>
				{t('Eller skapa ett konto med email och lösenord')}
			</Text>
			<form onSubmit={form.handleSubmit(onSubmit)}>
				<Flex gap={3} mb={3}>
					<FormControl isInvalid={!!form.formState.errors.email} mb={1}>
						<Input
							type="email"
							placeholder={t('Email')}
							{...form.register('email', {
								required: t('Email är nödvändig.') || 'Email är nödvändig.',
							})}
							borderColor="blackAlpha.600"
						/>
						{form.formState.errors.email && (
							<FormErrorMessage my={3}>
								{form.formState.errors.email.message}
							</FormErrorMessage>
						)}
					</FormControl>
					<FormControl isInvalid={!!form.formState.errors.password} mb={1}>
						<Input
							type="password"
							placeholder={t('Lösenord')}
							{...form.register('password', {
								required:
									t('lösenord är nödvändig.') || 'lösenord är nödvändig.',
							})}
							borderColor="blackAlpha.600"
						/>
						{form.formState.errors.password && (
							<FormErrorMessage my={3}>
								{form.formState.errors.password.message}
							</FormErrorMessage>
						)}
					</FormControl>
				</Flex>
				{error && <Text color="red.500">{error.message}</Text>}
				<Button
					w="full"
					type="submit"
					isLoading={form.formState.isSubmitting}
					mb={6}
				>
					{t('Skapa konto för att se pris')}
				</Button>
			</form>
			<ContentFooter>
				{t(
					'Ta hjälp av våra kontorsexperter för att se till att ni hittar bästa möjliga kontor för ert team, helt kostnadsfritt.'
				)}
			</ContentFooter>
		</>
	)
}

type SignInFormType = {
	email: string
	password: string
}

const SignInForm: FC<{ closeModal: () => void }> = ({ closeModal }) => {
	const { t } = useTranslation()
	const { register, formState, handleSubmit } = useForm<SignInFormType, any>()
	const {
		passwordSignIn,
		googleSignIn,
		microsoftSignIn,
		error: firebaseError,
	} = useSignInUpForm({
		defaultType: 'signIn',
	})
	const onSubmit = useCallback(
		async (data: SignInFormType) => {
			await passwordSignIn(data.email, data.password)
			closeModal()
		},
		[passwordSignIn, closeModal]
	)
	return (
		<>
			<Text fontSize="sm" mb={2}>
				{t(
					'Logga in på ditt konto för att se priser, lägga till kontor, och boka visningar'
				)}
			</Text>
			{firebaseError && (
				<Text color="red.500" fontWeight="bold">
					{firebaseError.message}
				</Text>
			)}
			<Box pt={3} mb={{ base: 6, md: 3 }}>
				<Text color="blackAlpha.700" mb={2}>
					{t('Logga in med')}:
				</Text>
				<Flex
					direction={{ base: 'column', md: 'row' }}
					gap={{ base: 1, md: 3 }}
				>
					<GoogleSignInButton
						h="2.625rem"
						mb={{ base: 3, md: 0 }}
						onClick={async () => {
							await googleSignIn()
							closeModal()
						}}
					/>
					<MicrosoftSignInButton
						h="2.625rem"
						onClick={async () => {
							await microsoftSignIn()
							closeModal()
						}}
					/>
				</Flex>
			</Box>
			<Box pt={3}>
				<Text color="blackAlpha.700" mb={2}>
					{t('Eller använd')}:
				</Text>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Flex gap={3} mb={3}>
						<FormControl isInvalid={!!formState.errors.email} mb={1}>
							<Input
								type="email"
								placeholder={t('Email')}
								{...register('email', {
									required: t('Email är nödvändig.') || 'Email är nödvändig.',
								})}
								borderColor="blackAlpha.600"
							/>
							{formState.errors.email && (
								<FormErrorMessage my={3}>
									{formState.errors.email.message}
								</FormErrorMessage>
							)}
						</FormControl>
						<FormControl isInvalid={!!formState.errors.password} mb={1}>
							<Input
								type="password"
								placeholder={t('Lösenord')}
								{...register('password', {
									required:
										t('lösenord är nödvändig.') || 'lösenord är nödvändig.',
								})}
								borderColor="blackAlpha.600"
							/>
							{formState.errors.password && (
								<FormErrorMessage my={3}>
									{formState.errors.password.message}
								</FormErrorMessage>
							)}
						</FormControl>
					</Flex>
					<Button w="full" type="submit" mt={5}>
						{t('Logga in för att se pris')}
					</Button>
				</form>
			</Box>
		</>
	)
}

function useSolutionDetails(
	solution: CoworkingSolution | undefined,
	location: LocationResult
) {
	const { t } = useTranslation()
	const rooms = useParallelQueries(
		getRoomQueries(solution?.rooms || [], !!solution)
	)
	const services = useQuery(servicesQuery)
	const isFurnshed =
		services.data &&
		services.data
			.filter((s) => location.services.includes(s.id))
			.find((s) => s.name == 'furnished')
	const hasBreakfast =
		services.data &&
		services.data
			.filter((s) => location.services.includes(s.id))
			.find((s) => s.name == 'breakfast')
	const jointedRoomsName = useMemo(() => {
		if (!rooms.data) {
			return ''
		}
		return rooms.data.length > 0
			? rooms.data.map((room) => room.external_id).join(' • ')
			: ''
	}, [rooms])

	return {
		jointedRoomsName,
		locationPerks: [
			t('Tillgång till {{location_size}}m² gemensamma ytor', {
				location_size: location.size.toString(),
			}),
			t('Tillgång till {{number_of_meeting_rooms}}st mötesrum', {
				number_of_meeting_rooms: location.number_of_meeting_rooms,
			}),
			t('Korta avtalstider'),
			t(
				'Allt ingår - {{furnished}}städning, kaffe, internet{{breakfast}} och mer!',
				{
					furnished: isFurnshed ? `${t('möbler')}, ` : '',
					breakfast: hasBreakfast ? `, ${t('frukost')}` : '',
				}
			),
		],
	}
}
const SolutionDetails: FC<{
	location: LocationResult
	solution: CoworkingSolution
}> = ({ location, solution }) => {
	const { t } = useTranslation()
	const { jointedRoomsName, locationPerks } = useSolutionDetails(
		solution,
		location
	)
	const solutionFloorId = useSolutionFloorId(solution.rooms[0] || '')
	const floorsWithBounds = useFloorsWithBounds(location)
	return (
		<>
			<ConfimationPriceInfo
				name={`${t('Kontor')} ${jointedRoomsName}`}
				price={solution.price}
				priceSuffix={t('kr/månad')}
			/>
			<Flex direction={{ base: 'column-reverse', md: 'row' }} gap={2}>
				<Box>
					<SolutionMap
						solutionFloor={solutionFloorId || undefined}
						activeRooms={solution.rooms}
						floors={floorsWithBounds}
						height={186}
					/>
				</Box>
				<Box>
					<Text fontSize="md" fontWeight="bold" mb={1}>
						{`${t('Kontor')} | ${jointedRoomsName}`}
					</Text>
					<Flex mb={{ base: 2, md: 3 }} gap={3}>
						<Text>
							{`${t('Platser')}: `}
							<b>{solution.workspaces}</b>
						</Text>
						<Text>
							{`${t('Storlek')}: `}
							<b>{solution.size}m²</b>
						</Text>
					</Flex>
					<Flex flexDirection="column" gap={3}>
						{locationPerks.map((p, i) => (
							<Grid key={`perk-${i}`} gridTemplateColumns="20px auto" gap={2}>
								<GridItem>
									<Box
										borderRadius="full"
										height="20px"
										width="20px"
										border="0.5px solid #ECEFF4"
										p="2px"
									>
										<StarVerifiedIcon
											sx={{ path: { fill: 'grey.600' } }}
											height="15px"
											width="15px"
										/>
									</Box>
								</GridItem>
								<GridItem>
									<Text fontSize="12px">{p}</Text>
								</GridItem>
							</Grid>
						))}
					</Flex>
				</Box>
			</Flex>
		</>
	)
}

const MembershipDetails: FC<{
	membership: ServiceOfficeMembershipResult
	location: LocationResult
}> = ({ membership, location }) => {
	const { i18n, t } = useTranslation()

	return (
		<Box h="full">
			<ConfimationPriceInfo
				name={`${t('Medlemskap')} | ${membership.name}`}
				price={membership.price}
				priceSuffix={t('kr/person/mån')}
			/>
			<Text fontSize="md" fontWeight="bold" mb={{ base: 2, md: 3 }}>
				{membership.name}
			</Text>
			<Text>
				{i18n.language != 'en'
					? membership.description
					: membership.description_en}
			</Text>
			<br />
			<Text>
				{t(
					'Medlemskap som erbjuds på {{location_name}}. Samtliga medlemskap säljs per månad. Hyrs antingen självständigt eller i kombination med kontorsrum som extramedlemskap vid behov av mer kapacitet.',
					{ location_name: location.name }
				)}
			</Text>
		</Box>
	)
}

const FlexOfficeDetails: FC<{
	flexOffice: FlexOfficeResultWithCheapestOffer
}> = ({ flexOffice }) => {
	const { t } = useTranslation()
	return (
		<>
			{flexOffice.fromPrice && (
				<ConfimationPriceInfo
					name={`${t('Kontor')} ${flexOffice.external_id} ${t('Från')}`}
					price={flexOffice.fromPrice}
					priceSuffix={t('kr/månad')}
				/>
			)}
			<Flex direction="column" gap={2}>
				<Text fontSize="md" fontWeight="bold" mb={1}>
					{`${t('Kontor')} | ${flexOffice.external_id}`}
				</Text>
				<Flex mb={{ base: 2, md: 3 }} gap={3}>
					<Text>
						{`${t('Platser')}: `}
						<b>{flexOffice.max_workspaces}</b>
					</Text>
					<Text>
						{`${t('Storlek')}: `}
						<b>{flexOffice.size}m²</b>
					</Text>
				</Flex>
			</Flex>
		</>
	)
}

const ConfimationPriceInfo: FC<{
	name: string
	price: number
	priceSuffix: string
}> = ({ name, price, priceSuffix }) => {
	return (
		<Box backgroundColor="#ECEFF4" textAlign="center" py={4} mb={6}>
			<Text fontSize="lg" fontWeight="bold">
				{name}
			</Text>
			<Text
				fontSize="lg"
				fontWeight="bold"
				color="blue.500"
				mb={{ base: 2, md: 3 }}
			>
				{`${formatInt(price)} ${priceSuffix}`}
			</Text>
		</Box>
	)
}

const LargeTeamFooter: FC = () => {
	const { t } = useTranslation()
	const todaysExpert = usePersonOfTheDay()

	return (
		<ModalFooter
			background="blue.500"
			borderBottomRadius="md"
			px={{ base: 0, md: 12 }}
			py={{ base: 2, md: 4 }}
		>
			<Grid
				templateRows="repeat(2, auto)"
				templateColumns={{ base: '60px auto', md: '80px auto' }}
				rowGap={{ base: 3, md: 1 }}
				columnGap={{ base: 2, md: 5 }}
				color="white"
				my={5}
				mx={5}
			>
				<GridItem
					rowSpan={{ base: 1, md: 2 }}
					colSpan={1}
					borderRadius="full"
					position="relative"
					maxW={{ base: '60px', md: '80px' }}
					w={{ base: '60px', md: '80px' }}
					h={{ base: '60px', md: '80px' }}
				>
					<Image
						src={todaysExpert.img}
						borderRadius="full"
						maxW={{ base: '60px', md: '80px' }}
						w={{ base: '60px', md: '80px' }}
						h={{ base: '60px', md: '80px' }}
						alt="Kontorsexpert"
					/>
					<Box
						padding={1}
						borderRadius="full"
						bgColor="white"
						position="absolute"
						right={0}
						bottom={0}
					>
						<StarVerifiedIcon
							h={{ base: '12px', md: '15px' }}
							w={{ base: '12px', md: '15px' }}
						/>
					</Box>
				</GridItem>
				<GridItem
					rowSpan={{ base: 1 }}
					colSpan={{ base: 1 }}
					alignSelf="center"
				>
					<Text fontSize={{ base: 'lg', md: '2xl' }}>
						{t('Skräddarsy ett kontor för ert team')}
					</Text>
				</GridItem>
				<GridItem rowSpan={{ base: 1 }} colSpan={{ base: 2, md: 1 }}>
					<Text fontSize="sm" fontWeight="normal">
						{t(
							'För team i storleken av 11-20 arbetsplatser rekommenderar vi ett kostnadsfritt konsultativt möte där våra förmedlare tar fram skräddarsydda förslag utefter era behov.'
						)}
					</Text>
					<Button
						as={Link}
						variant="ghost"
						href={CALENDLY_LINK}
						target="_blank"
						w="100%"
						mt={5}
						color="blue.500"
						background="white"
						onClick={() => {
							sendGA4Event('click_book_meeting', {
								category: 'engagement',
								location: 'post_interest_account',
							})
						}}
					>
						{t('Boka kostnadsfritt möte')}
					</Button>
				</GridItem>
			</Grid>
		</ModalFooter>
	)
}

const Confirmation: FC<{
	location: LocationResult
	solution?: CoworkingSolution | undefined
	membership?: ServiceOfficeMembershipResult | undefined
	flexOffice?: FlexOfficeResultWithCheapestOffer | undefined
	onShowingClick: () => void
	onClose: () => void
}> = ({
	location,
	solution,
	membership,
	onShowingClick,
	onClose,
	flexOffice,
}) => {
	const { t } = useTranslation()
	const params = useCoworkingSerpParams()
	const providerResult = useQuery(getProviderQuery(location.provider))
	const { data: user } = useUser()

	useEffect(() => {
		// We store the state 'hasSentGA4Event' in localStorage in order
		// to avoid the GA4 event to be called multiple times.
		// We want to make sure that user is inlogged because the price is
		// otherwise 0.
		if (!localStorage.getItem('hasSentGA4Event') && user) {
			const sizeSegmentInLocalStorage = localStorage.getItem(
				'interest_workspaces'
			)
			if (!sizeSegmentInLocalStorage) {
				return
			}
			const sizeSegment = JSON.parse(sizeSegmentInLocalStorage) as {
				minWorkspaces: number
				maxWorkspaces: number
			}
			const getValue = () => {
				if (solution) {
					return (solution.price * 12) / 250
				}
				if (membership) {
					return (membership.price * 12) / 250
				}
				if (flexOffice && flexOffice.fromPrice) {
					return (flexOffice.fromPrice * 12) / 250
				}
				if (params.budget) {
					return (params.budget * 12) / 250
				}
				const maxWs = sizeSegment.maxWorkspaces
				return maxWs <= 2
					? 250
					: maxWs <= 5
					? 1000
					: maxWs <= 10
					? 2500
					: maxWs <= 20
					? 5000
					: 10000
			}
			sendGA4Event('price_account', {
				category: 'lead',
				budget: params.budget || 0,
				size:
					params.min_workspaces ||
					params.max_workspaces ||
					sizeSegment.maxWorkspaces
						? `${sizeSegment.minWorkspaces}-${sizeSegment.maxWorkspaces}`
						: `${sizeSegment.minWorkspaces}+`,
				city: location.breadcrumbs.city.name,
				value: getValue(),
				currency: 'sek',
				operator: providerResult.data?.name || 'failed_to_fetch_provider',
				location: location.name || 'failed_to_fetch_location',
			})
			localStorage.setItem('hasSentGA4Event', 'true')
		}
	}, [
		solution,
		location,
		membership,
		params,
		providerResult.data?.name,
		user,
		flexOffice,
	])
	return !(
		solution == undefined &&
		membership == undefined &&
		flexOffice == undefined
	) ? (
		<ModalContent minH={{ base: 'auto', md: '614px' }}>
			<ModalCloseButton />
			<Box py={{ base: 2, md: 4 }} px={{ base: 0, md: 12 }}>
				<ModalHeader mt={5}>
					<Text>{t('Check - Nu har du tillgång till alla priser!')}</Text>
				</ModalHeader>
				<Divider
					mx="auto"
					w={{ base: '90%', md: '92%' }}
					bgColor="blackAlpha.300"
					mb={{ base: 3, md: 3 }}
				/>
				<ModalBody>
					<Text fontSize="lg" fontWeight="bold" mb={6}>
						{t('Intresserad utav kontoret? Boka en förutsättningslös visning')}
					</Text>
					{solution && (
						<SolutionDetails solution={solution} location={location} />
					)}
					{membership && (
						<MembershipDetails membership={membership} location={location} />
					)}
					{flexOffice && <FlexOfficeDetails flexOffice={flexOffice} />}
					<Button
						onClick={() => {
							sendGA4Event('initiate_showing', {
								step: 'initiate',
								location: 'post_interest_account',
							})
							onShowingClick()
						}}
						width="100%"
						mt={9}
					>
						{t('Önska visning')}
					</Button>
					<Flex
						alignItems="center"
						gap={3}
						backgroundColor="blue.50"
						mt={6}
						mb={{ base: 3, md: 5 }}
						p={4}
					>
						<IcnLightStick width={6} height={6} />
						<Text>
							{t(
								'Ditt konto är nu skapat. Du har nu tillgång till samtliga uppdaterade priser på över 10 000 kontor och hundratals med kontorshotell.'
							)}
						</Text>
					</Flex>
				</ModalBody>
			</Box>
			{((solution && solution.workspaces > 9) || flexOffice) && (
				<LargeTeamFooter />
			)}
		</ModalContent>
	) : (
		<ModalContent minH="auto">
			<ModalCloseButton />
			<Box py={{ base: 2, md: 4 }} px={{ base: 0, md: 12 }}>
				<ModalHeader mt={5}>
					<Text>{t('Check - Nu har du tillgång till alla priser!')}</Text>
				</ModalHeader>
				<Divider
					mx="auto"
					w={{ base: '90%', md: '92%' }}
					bgColor="blackAlpha.300"
					mb={{ base: 3, md: 3 }}
				/>
				<ModalBody>
					<ConversionBoxConfirmationContent
						location={location}
						closeModal={onClose}
					/>
				</ModalBody>
			</Box>
		</ModalContent>
	)
}

const ConversionBoxConfirmationContent: FC<{
	location: LocationResult
	closeModal: () => void
}> = ({ location, closeModal }) => {
	const { t } = useTranslation()
	const providerResult = useQuery(getProviderQuery(location.provider))
	const buttonTexts = useBreakpointValue({
		base: { primary: t('Boka möte'), secondary: t('Visa priser') },
		md: {
			primary: t('Boka kostnadsfritt möte'),
			secondary: t('Visa {{provider_name}}s priser', {
				provider_name: providerResult.data ? providerResult.data.name : '',
			}),
		},
	})
	return (
		<>
			<Flex
				alignItems="center"
				gap={3}
				backgroundColor="blue.50"
				mb={{ base: 3, md: 5 }}
				p={4}
			>
				<IcnLightStick width={6} height={6} />
				<Text>
					{t(
						'Ditt konto är nu skapat. Du har nu tillgång till samtliga uppdaterade priser på över 10 000 kontor och hundratals med kontorshotell.'
					)}
				</Text>
			</Flex>
			<Box>
				<Text fontSize="lg" fontWeight="bold" mb={{ base: 2, md: 3 }}>
					{t('Offertbegäran för {{location_name}}', {
						location_name: location.name,
					})}
				</Text>
				<Text mb={3}>
					{t(
						'Med ditt konto har du redan fått tillgång till priserna för {{location_name}}, som du enkelt når genom att gå tillbaka till annonsen och spana in lösningarna. Utöver det hör vi av oss inom kort för att ge dig en prisbild utefter just era förutsättningar.',
						{ location_name: location.name }
					)}
				</Text>
				<Text fontSize="lg" fontWeight="bold" mb={{ base: 2, md: 3 }}>
					{t('Låt våra experter göra jobbet åt er')}
				</Text>
				<Text mb={6}>
					{t(
						'Boka ett möte med en utav våra kontorsexperter så hjälper vi er sy ihop en visningsrunda på 5 alternativ som passar ert team - utan att ni behöver lyfta ett finger. 100% kostnadsfritt.'
					)}
				</Text>
				<Flex gap={2} my={{ base: 5, md: 10 }}>
					<Button as="a" href={CALENDLY_LINK} w="full">
						{buttonTexts?.primary}
					</Button>
					<Button variant="outline" w="full" onClick={closeModal}>
						{buttonTexts?.secondary}
					</Button>
				</Flex>
				<ContentFooter>
					{t(
						'Ta hjälp av våra kontorsexperter för att se till att ni hittar bästa möjliga kontor för ert team, helt kostnadsfritt.'
					)}
				</ContentFooter>
			</Box>
		</>
	)
}

const ShowPriceModal: FC<{
	location: LocationResult
	membership?: ServiceOfficeMembershipResult | undefined
	solution?: CoworkingSolution | undefined
	flexOffice?: FlexOfficeResultWithCheapestOffer | undefined
	isOpen: boolean
	onClose: () => void
}> = ({ location, isOpen, onClose, membership, solution, flexOffice }) => {
	const { t } = useTranslation()
	const params = useCoworkingSerpParams()
	const workspaces = params.min_workspaces || params.max_workspaces
	const serpParamSizeValue = workspaces
		? COMPANY_SIZE_CHOICES.find((choice) => workspaces <= choice.max)
		: undefined

	const createAccountLocation:
		| 'solution list'
		| 'membership'
		| 'conversion box'
		| 'flex office' = solution
		? 'solution list'
		: membership
		? 'membership'
		: flexOffice
		? 'flex office'
		: 'conversion box'
	const {
		currentStep,
		lastStep,
		goToNextStep,
		goToPreviousStep,
		isSignInFormShown,
		showSignUpForm,
		showSignInForm,
		sendInterest,
	} = useShowPriceForm(location, createAccountLocation)

	const localMoveInDate =
		typeof window !== 'undefined'
			? localStorage.getItem('interest_moveInDate')
			: null
	const localSizeStr =
		typeof window !== 'undefined'
			? localStorage.getItem('interest_workspaces')
			: null
	const defaultValues = useMemo(() => {
		const localSize = localSizeStr
			? (JSON.parse(localSizeStr) as {
					minWorkspaces: number
					maxWorkspaces: number
			  })
			: undefined
		const minWorkspaces = localSize
			? localSize.minWorkspaces
			: serpParamSizeValue
			? serpParamSizeValue.min
			: undefined
		const maxWorkspaces = localSize
			? localSize.maxWorkspaces
			: serpParamSizeValue
			? serpParamSizeValue.max
			: undefined

		if (!localMoveInDate && !minWorkspaces) {
			return undefined
		}
		const defaultValues =
			minWorkspaces && maxWorkspaces
				? { minWorkspaces, maxWorkspaces }
				: undefined

		if (localMoveInDate) {
			return {
				...defaultValues,
				moveInDate: localMoveInDate as MoveInDateForInterest,
			}
		}

		return defaultValues
	}, [serpParamSizeValue, localMoveInDate, localSizeStr])

	const form = useForm<FormValues>(
		defaultValues
			? {
					defaultValues,
			  }
			: {}
	)
	const bookShowingModalProps = useBookShowingModalProps(
		location,
		membership,
		solution,
		flexOffice
	)

	const modalHeaderText = useBreakpointValue({
		base:
			createAccountLocation === 'conversion box'
				? t('Skapa konto för att få en offert')
				: t('Skapa konto för att se priser'),
		md:
			createAccountLocation === 'conversion box'
				? t('Skapa ett kostnadsfritt konto för att få en offert')
				: t('Skapa ett kostnadsfritt konto för att se priser'),
	})
	return (
		<Modal
			size={{ base: 'full', md: currentStep <= lastStep ? '2xl' : 'md' }}
			isOpen={isOpen}
			onClose={() => onClose()}
			preserveScrollBarGap={true}
		>
			<ModalOverlay />
			{currentStep < lastStep ? (
				<ModalContent
					minH={{ base: 'auto', md: '614px' }}
					py={{ base: 2, md: 4 }}
					px={{ base: 0, md: 12 }}
				>
					<ModalCloseButton onClick={onClose} />
					<ModalHeader mt={5}>
						<Flex
							w="full"
							display="flex"
							justifyContent="space-between"
							alignItems="end"
						>
							<Text>
								{isSignInFormShown
									? t('Logga in')
									: currentStep > lastStep
									? t('Ditt konto är skapat')
									: modalHeaderText}
							</Text>
							{!isSignInFormShown && (
								<Text fontSize="xs" color="blackAlpha.700">{`${
									currentStep > lastStep ? lastStep : currentStep
								}/${lastStep}`}</Text>
							)}
						</Flex>
					</ModalHeader>
					<Divider
						mx="auto"
						w={{ base: '90%', md: '92%' }}
						bgColor="blackAlpha.300"
						mb={{ base: 3, md: 3 }}
					/>
					<ModalBody>
						{isSignInFormShown ? (
							<SignInForm closeModal={onClose} />
						) : currentStep == 1 ? (
							<MoveInDateForm
								city={location.address.city}
								goToNext={goToNextStep}
								form={form}
								createAccountLocation={createAccountLocation}
							/>
						) : currentStep == 2 ? (
							<CompanySizeForm
								city={location.address.city}
								goToNext={goToNextStep}
								form={form}
							/>
						) : currentStep == 3 ? (
							<SignUpForm
								goToNext={goToNextStep}
								form={form}
								sendInterest={sendInterest}
							/>
						) : (
							<></>
						)}
					</ModalBody>
					<ModalFooter
						justifyContent={
							!isSignInFormShown && currentStep >= 2 && currentStep < lastStep
								? 'space-between'
								: 'flex-end'
						}
						alignItems="center"
					>
						{!isSignInFormShown && currentStep >= 2 && currentStep <= lastStep && (
							<Button
								leftIcon={
									<ArrowLeftIcn
										w={3}
										mr={2}
										sx={{ path: { strokeWidth: 5 } }}
									/>
								}
								variant="ghost"
								pl={0}
								colorScheme="gray"
								fontSize="sm"
								fontWeight="medium"
								onClick={goToPreviousStep}
							>
								{t('Tillbaka')}
							</Button>
						)}
						{currentStep < lastStep && (
							<Text>
								<Trans
									i18nKey={
										isSignInFormShown
											? 'Har du inget konto, skapa ett konto här.'
											: 'Har du redan ett konto? Logga in här.'
									}
									defaults={
										isSignInFormShown
											? 'Har du inget konto, <button>skapa ett konto här.</button>'
											: 'Har du redan ett konto? <button>Logga in här.</button>'
									}
									components={{
										button: (
											<Button
												variant="link"
												onClick={
													isSignInFormShown ? showSignUpForm : showSignInForm
												}
												fontSize="sm"
												textDecoration="underline"
											/>
										),
									}}
								/>
							</Text>
						)}
					</ModalFooter>
				</ModalContent>
			) : currentStep == lastStep ? (
				<Confirmation
					location={location}
					solution={solution}
					membership={membership}
					flexOffice={flexOffice}
					onShowingClick={goToNextStep}
					onClose={onClose}
				/>
			) : (
				<BookShowingModalContent {...bookShowingModalProps} />
			)}
		</Modal>
	)
}

function useBookShowingModalProps(
	location: LocationResult,
	membership?: ServiceOfficeMembershipResult | undefined,
	solution?: CoworkingSolution | undefined,
	flexOffice?: FlexOfficeResultWithCheapestOffer | undefined
): BookShowingModalContentProps {
	if (membership) {
		return { membership }
	}
	if (solution) {
		return { solution }
	}
	if (flexOffice) {
		return { flexoffice: flexOffice }
	}
	return { location }
}

export function useShowPriceModal(
	type: 'membership' | 'solution' | 'conversion-box' | 'flex office',
	id: string
): {
	isShowPriceModalOpen: boolean
	onShowPriceModalOpen: () => void
	onShowPriceModalClose: () => void
} {
	const { pathname, search } = useLocation()
	const history = useHistory()

	const isShowPriceModalOpen =
		new URLSearchParams(search).get('interest') === `${type}-${id}`

	const onShowPriceModalOpen = () => {
		const toParams = new URLSearchParams(search)
		toParams.append('interest', `${type}-${id}`)

		history.replace(`${pathname}?${toParams.toString()}`)
	}

	const onShowPriceModalClose = () => {
		const to = new URLSearchParams(search)
		to.delete('interest')
		to.delete('step')
		history.replace(
			`${pathname}${to.toString().length ? `?${to.toString()}` : ''}`
		)
	}

	return {
		isShowPriceModalOpen,
		onShowPriceModalOpen,
		onShowPriceModalClose,
	}
}

const ShowPriceButton: FC<
	PropsWithChildren &
		ChakraProps &
		ThemingProps & {
			GA4EventLocation:
				| 'solution list'
				| 'membership'
				| 'conversion box'
				| 'flex office'
			onClick?: () => void
		}
> = ({ GA4EventLocation, onClick, children, ...rest }) => {
	return (
		<>
			<Button
				variant="outline"
				onClick={() => {
					if (onClick) {
						onClick()
					}
					sendGA4Event('initiate_price_account', {
						step: 'initiate',
						location: GA4EventLocation,
					})
				}}
				{...rest}
			>
				{children}
			</Button>
		</>
	)
}

export { ShowPriceModal, ShowPriceButton }
